import { useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ArrowLeft, Sparkles, CheckCircle2 } from 'lucide-react';

const API = `${window.location.origin}/api`;

export default function PerfumeCheckout() {
    const { slug } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const cart = location.state?.cart || [];
    const shop = location.state?.shop || {};
    const primary = shop.primary_color || '#D4AF37';
    const bg = shop.background_color || '#0F0F1A';
    const font = shop.font_family || 'Cormorant Garamond';

    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [note, setNote] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [order, setOrder] = useState(null);

    const total = cart.reduce((s, i) => s + i.price * i.qty, 0);

    const handleSubmit = async () => {
        if (!name.trim()) return;
        setSubmitting(true);
        try {
            const res = await axios.post(`${API}/catalog/${slug}/order`, {
                items: cart.map(i => ({ id: i.id, name: i.name, brand: i.brand, price: i.price, qty: i.qty })),
                customer_name: name, customer_phone: phone, customer_note: note
            });
            setOrder(res.data);
        } catch { /* */ }
        finally { setSubmitting(false); }
    };

    if (!cart.length && !order) {
        navigate(`/catalog/${slug}`);
        return null;
    }

    return (
        <div className="min-h-screen" style={{ backgroundColor: bg, fontFamily: `'${font}', serif` }} data-testid="perfume-checkout">
            <link rel="stylesheet" href={`https://fonts.googleapis.com/css2?family=${encodeURIComponent(font)}:wght@300;400;500;600;700&display=swap`} />

            {/* Back */}
            {!order && (
                <div className="px-4 py-3">
                    <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white/40 text-sm"><ArrowLeft className="w-4 h-4" /> Back to Cart</button>
                </div>
            )}

            <div className="max-w-md mx-auto px-4 py-8">
                {order ? (
                    /* Order Confirmation */
                    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center" data-testid="order-confirmation">
                        <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ backgroundColor: `${primary}20` }}>
                            <CheckCircle2 className="w-10 h-10" style={{ color: primary }} />
                        </div>
                        <h1 className="text-2xl font-bold text-white mb-2">Order Placed!</h1>
                        <p className="text-white/40 text-sm mb-2">Your order has been received</p>
                        <span className="inline-block px-3 py-1 rounded-full text-[10px] font-medium bg-white/[0.05] border border-white/[0.08] text-white/40 mb-6">Cash on Delivery</span>
                        <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-5 mb-6">
                            <p className="text-xs text-white/30 uppercase tracking-wider mb-1">Order Number</p>
                            <p className="text-4xl font-mono font-bold tracking-wider" style={{ color: primary }} data-testid="order-number">#{order.id}</p>
                        </div>
                        <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-4 mb-6">
                            {(order.items || []).map((item, i) => (
                                <div key={i} className="flex justify-between py-2 border-b border-white/[0.04] last:border-0">
                                    <span className="text-sm text-white/60">{item.qty}x {item.name}</span>
                                    <span className="text-sm" style={{ color: primary }}>QAR {(item.price * item.qty).toFixed(0)}</span>
                                </div>
                            ))}
                            <div className="flex justify-between pt-3 mt-2 border-t border-white/[0.08]">
                                <span className="text-white/40 font-medium">Total</span>
                                <span className="text-xl font-bold" style={{ color: primary }}>QAR {order.total}</span>
                            </div>
                        </div>
                        {/* WhatsApp Share */}
                        <button
                            onClick={() => {
                                const items_text = (order.items || []).map(i => `${i.qty}x ${i.name}`).join(', ');
                                const msg = `Order ${order.id} placed at ${shop.name}!\nItems: ${items_text}\nTotal: QAR ${order.total}\nPayment: Cash on Delivery`;
                                window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
                            }}
                            className="w-full py-3 rounded-2xl text-white font-medium mb-3 flex items-center justify-center gap-2"
                            style={{ backgroundColor: '#25D366' }}
                            data-testid="share-order-whatsapp"
                        >
                            Share via WhatsApp
                        </button>
                        <button onClick={() => navigate(`/catalog/${slug}`)} className="w-full py-4 rounded-2xl text-white font-semibold border border-white/[0.08]" data-testid="continue-shopping-btn">
                            Continue Shopping
                        </button>
                    </motion.div>
                ) : (
                    /* Checkout Form */
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                        <div className="text-center mb-8">
                            <div className="w-14 h-14 rounded-2xl mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: primary }}><Sparkles className="w-7 h-7 text-white" /></div>
                            <h1 className="text-2xl font-bold text-white">Checkout</h1>
                            <p className="text-white/30 text-sm mt-1">{shop.name}</p>
                        </div>

                        {/* Order Summary */}
                        <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-4 mb-6">
                            <p className="text-xs text-white/30 uppercase tracking-wider mb-3">Order Summary</p>
                            {cart.map(item => (
                                <div key={item.id} className="flex justify-between py-2 border-b border-white/[0.04] last:border-0">
                                    <div><p className="text-sm text-white/70">{item.qty}x {item.name}</p><p className="text-xs text-white/30">{item.brand}</p></div>
                                    <span className="text-sm font-medium" style={{ color: primary }}>QAR {(item.price * item.qty).toFixed(0)}</span>
                                </div>
                            ))}
                            <div className="flex justify-between pt-3 mt-2 border-t border-white/[0.08]">
                                <span className="text-white/40 font-medium">Total</span>
                                <span className="text-2xl font-bold" style={{ color: primary }}>QAR {total.toFixed(0)}</span>
                            </div>
                        </div>

                        {/* Customer Info */}
                        <div className="space-y-4 mb-8">
                            <div>
                                <label className="text-xs text-white/30 uppercase tracking-wider block mb-1.5">Name *</label>
                                <input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" className="w-full px-4 py-3 rounded-xl bg-white/[0.05] border border-white/[0.08] text-white placeholder-white/20 text-sm focus:outline-none focus:border-white/20" data-testid="checkout-name" />
                            </div>
                            <div>
                                <label className="text-xs text-white/30 uppercase tracking-wider block mb-1.5">Phone</label>
                                <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="Phone number" className="w-full px-4 py-3 rounded-xl bg-white/[0.05] border border-white/[0.08] text-white placeholder-white/20 text-sm focus:outline-none focus:border-white/20" data-testid="checkout-phone" />
                            </div>
                            <div>
                                <label className="text-xs text-white/30 uppercase tracking-wider block mb-1.5">Note</label>
                                <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Any special requests..." rows={2} className="w-full px-4 py-3 rounded-xl bg-white/[0.05] border border-white/[0.08] text-white placeholder-white/20 text-sm focus:outline-none focus:border-white/20 resize-none" />
                            </div>
                        </div>

                        <button onClick={handleSubmit} disabled={submitting || !name.trim()} className="w-full py-4 rounded-2xl text-white font-semibold text-lg disabled:opacity-50" style={{ backgroundColor: primary }} data-testid="place-order-btn">
                            {submitting ? 'Placing Order...' : `Place Order — QAR ${total.toFixed(0)}`}
                        </button>
                        <div className="text-center mt-3">
                            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.03] border border-white/[0.06] text-xs text-white/30">
                                Cash on Delivery
                            </span>
                        </div>
                    </motion.div>
                )}
            </div>
        </div>
    );
}
